import { useState, useEffect } from 'react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import type CrmCoreTeam from '@/types/serializers/CrmCoreTeam'
import type CrmFundraisingCampaign from '@/types/serializers/CrmFundraisingCampaign'
import { EditCampaignForm } from './EditCampaignForm'
import type { AssignableTeam } from './TeamGroupedUserPicker'

interface EditCampaignSheetProps {
  campaignId: number | null
  onOpenChange: (open: boolean) => void
  teams: CrmCoreTeam[]
  assignableTeams: AssignableTeam[]
}

interface DetailResponse {
  campaign: CrmFundraisingCampaign
  assignable_teams: AssignableTeam[]
}

// Loads campaign detail (with team_ids/user_ids) via the #show JSON branch
// when opened, then renders the seeded edit form. Shows a skeleton while the
// fetch is in flight and an inline retry on failure.
export function EditCampaignSheet({
  campaignId,
  onOpenChange,
  teams,
  assignableTeams,
}: EditCampaignSheetProps) {
  const [data, setData] = useState<DetailResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!campaignId) {
      setData(null)
      setError(false)
      return
    }
    setLoading(true)
    setError(false)

    const controller = new AbortController()
    fetch(`/fundraising/campaigns/${campaignId}`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load')
        return res.json()
      })
      .then((json) => setData(json))
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error(err)
          setError(true)
        }
      })
      .finally(() => setLoading(false))

    return () => controller.abort()
  }, [campaignId, reloadKey])

  return (
    <Sheet open={!!campaignId} onOpenChange={onOpenChange}>
      <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Modifier la campagne</SheetTitle>
          <SheetDescription>
            Modifiez les informations et les affectations.
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="space-y-4 p-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : error ? (
          <div className="space-y-3 p-4">
            <p className="text-sm text-destructive">
              Le chargement de la campagne a échoué.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => setReloadKey((k) => k + 1)}
            >
              Réessayer
            </Button>
          </div>
        ) : data ? (
          <EditCampaignForm
            key={data.campaign.id}
            campaign={data.campaign}
            teams={teams}
            assignableTeams={data.assignable_teams ?? assignableTeams}
            onDone={() => onOpenChange(false)}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  )
}
